import { useCallback, useMemo } from 'react';
import type { SelectOptionList } from '@cbhq/cds-web/alpha/select';
import { Select } from '@cbhq/cds-web/alpha/select';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

const messages = defineMessages({
  title: {
    id: 'Security.approvalRulesScope.title',
    defaultMessage: 'Scope',
    description: 'Title for the approval rules scope selector',
  },
  legalEntityAccessibilityLabel: {
    id: 'Security.approvalRulesScope.legalEntityAccessibilityLabel',
    defaultMessage: 'Legal entity',
    description: 'Accessibility label for the legal entity dropdown',
  },
  legalEntityPlaceholder: {
    id: 'Security.approvalRulesScope.legalEntityPlaceholder',
    defaultMessage: 'Select entity',
    description: 'Placeholder for the legal entity dropdown',
  },
});

const SELECT_STYLES = {
  dropdown: {
    padding: 8,
    width: 'max-content',
  },
  optionContent: {
    paddingTop: 2,
    paddingBottom: 2,
  },
} as const;

type ApprovalRulesScopeLegalEntity = {
  legalEntityId: string;
  legalEntityName: string;
};

type ApprovalRulesScopeProps = {
  legalEntities: ApprovalRulesScopeLegalEntity[];
  selectedLegalEntityId: string | null;
  onLegalEntityChange: (legalEntityId: string) => void;
};

export function ApprovalRulesScope({
  legalEntities,
  selectedLegalEntityId,
  onLegalEntityChange,
}: ApprovalRulesScopeProps) {
  const { formatMessage } = useIntl();

  const legalEntityOptions = useMemo<SelectOptionList<'single', string>>(
    () =>
      legalEntities.map(({ legalEntityId, legalEntityName }) => ({
        value: legalEntityId,
        label: legalEntityName,
      })),
    [legalEntities],
  );

  const handleLegalEntityChange = useCallback(
    (value: string | null) => {
      if (value === null) {
        return;
      }

      onLegalEntityChange(value);
    },
    [onLegalEntityChange],
  );

  return (
    <VStack background="bgSecondary" borderRadius={400} gap={2} padding={3} width="100%">
      <Text font="headline">{formatMessage(messages.title)}</Text>
      <HStack alignItems="center" gap={2} width="100%">
        <Select<'single', string>
          type="single"
          compact
          styles={SELECT_STYLES}
          value={selectedLegalEntityId}
          onChange={handleLegalEntityChange}
          options={legalEntityOptions}
          placeholder={formatMessage(messages.legalEntityPlaceholder)}
          controlAccessibilityLabel={formatMessage(messages.legalEntityAccessibilityLabel)}
        />
      </HStack>
    </VStack>
  );
}
